import type { ConsentState } from './types';

declare const __KOCKICA_VERSION__: string;

/** Injected from package.json at build time (tsup / vitest `define`), so it cannot drift. */
export const PACKAGE_VERSION: string = typeof __KOCKICA_VERSION__ === 'string' ? __KOCKICA_VERSION__ : '0.0.0-dev';

/**
 * One consent record. No IP, user agent or other personal data is sent from the browser;
 * the receiving server truncates the IP and derives the country itself.
 */
export interface ConsentLogRecord {
  siteId: string;
  consentId: string;
  revision: number;
  action: ConsentState['action'];
  accepted: string[];
  rejected: string[];
  language: string;
  /** Page where the choice was made, without query string or hash. */
  page: string;
  timestamp: string;
  sdkVersion: string;
}

export function buildLogRecord(siteId: string, state: ConsentState, location: Pick<Location, 'origin' | 'pathname'>): ConsentLogRecord {
  return {
    siteId,
    consentId: state.consentId,
    revision: state.revision,
    action: state.action,
    accepted: [...state.accepted],
    rejected: [...state.rejected],
    language: state.language,
    page: `${location.origin}${location.pathname}`,
    timestamp: state.timestamp,
    sdkVersion: PACKAGE_VERSION,
  };
}

/**
 * Send a record. Uses sendBeacon so it survives page navigation.
 * The body is sent as text/plain (a CORS "simple" request, so no preflight); the server parses it as JSON.
 */
export function sendLogRecord(endpoint: string, record: ConsentLogRecord): boolean {
  const body = JSON.stringify(record);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return true;
    }
    if (typeof fetch === 'function') {
      void fetch(endpoint, {
        method: 'POST',
        body,
        keepalive: true,
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(() => undefined);
      return true;
    }
  } catch {
    // Logging must never break the page; the choice itself is already stored in the visitor's cookie.
  }
  return false;
}
